/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
// import { AttributeValueBase } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
// import { StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { EmailRegex, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import { CheckoutModule, ErrorLocation } from '@msdyn365-commerce/global-state';
import classnames from 'classnames';
import { action, computed, reaction, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { focusOnCheckoutError } from '@msdyn365-commerce-modules/checkout';
import { ErrorComponent } from '@msdyn365-commerce-modules/checkout';

import { ILdsCheckoutContact4Data } from './lds-checkout-contact-4.data';
import { ILdsCheckoutContact4Props } from './lds-checkout-contact-4.props.autogenerated';
import getAddContactInfo, { IAddContactInfo } from './components/get-add-contact-info';
import getShowContactInfo, { IShowContactInfo } from './components/get-show-contact-info';

export * from './components/get-add-contact-info';
export * from './components/get-show-contact-info';

interface ICheckoutGuestProfileState {
    email: string;
    phone: string;
    phoneError: boolean;
    loading: boolean;
    hasCheckoutError?: boolean;
    errorMessage?: string;
}

export interface ICheckoutGuestProfileModuleProps extends ILdsCheckoutContact4Props<ILdsCheckoutContact4Data>, IModuleStateProps {}

export interface ICheckoutGuestProfileViewProps extends ILdsCheckoutContact4Props<ILdsCheckoutContact4Data>, ICheckoutGuestProfileState {
    moduleProps: IModuleProps;
    showContactInfo?: IShowContactInfo;
    addContactInfo?: IAddContactInfo;
    alert?: React.ReactNode;
    checkoutErrorRef?: React.RefObject<HTMLElement>;
    phone: any;
    phoneError: any;
    loading: boolean;

    onChange?(email: string): void;
    onChangePhone(phone: string, country?: string): void;
    onEdit?(): void;
    onSubmit?(): void;
    onCancel?(): void;
}

/**
 *
 * CheckoutGuestProfile component.
 * @extends {React.PureComponent<ILdsCheckoutContact4Props<ILdsCheckoutContact4Data>, ICheckoutGuestProfileState>}
 */
// @ts-expect-error
@withModuleState
@observer
class CheckoutGuestProfile extends React.Component<ICheckoutGuestProfileModuleProps, ICheckoutGuestProfileState> {
    public state: ICheckoutGuestProfileState = {
        email:
            (this.props.context &&
                this.props.context.request &&
                this.props.context.request.user &&
                this.props.context.request.user.isAuthenticated &&
                this.props.context.request.user.emailAddress) ||
            '',
        phone: '',
        phoneError: false,
        loading: false
    };

    private readonly inputRef: React.RefObject<HTMLInputElement> = React.createRef();

    private readonly checkoutErrorRef: React.RefObject<HTMLElement> = React.createRef();

    @computed get isDataReady(): boolean {
        return (this.props.data.checkout.result && this.props.data.checkout.status) === 'SUCCESS';
    }

    @computed get hasEmailError(): boolean {
        return this.props.moduleState.hasError && !this.state.hasCheckoutError;
    }

    public componentDidMount(): void {
        when(
            () => this.isDataReady,
            async () => {
                await this.init();
            }
        );

        reaction(
            () => this.props.data.checkout.result?.isExpressCheckoutApplied,
            async isExpressCheckoutApplied => {
                const emailAddressFromExpressCheckout = this.props.data.checkout.result?.emailAddressFromExpressCheckout ?? '';
                const isEmailFromExpressCheckoutDifferent =
                    !StringExtensions.isNullOrEmpty(emailAddressFromExpressCheckout) &&
                    emailAddressFromExpressCheckout !== this.state.email;

                if (
                    isExpressCheckoutApplied &&
                    (!this.props.data.checkout.result?.isExpressAddressApplied || isEmailFromExpressCheckoutDifferent)
                ) {
                    await this.submitEmail(emailAddressFromExpressCheckout);
                }
            }
        );

        if (this.props.data.checkout.result?.shouldEnableCheckoutErrorDisplayMessaging) {
            reaction(
                () => this.props.data.checkout.result?.checkoutError,
                checkoutError => {
                    if (checkoutError && checkoutError.errorLocation === ErrorLocation.CheckoutGuestProfile && checkoutError.errorMessage) {
                        this._setErrorMessage(checkoutError.errorMessage);
                    }
                }
            );

            reaction(
                () => this.props.data.checkout.result?.checkoutErrorFocus,
                checkoutErrorFocus => {
                    if (checkoutErrorFocus === CheckoutModule.CheckoutGuestProfile) {
                        void focusOnCheckoutError(this.checkoutErrorRef, this.props.context.actionContext);
                    }
                }
            );
        }
    }

    public shouldComponentUpdate(nextProps: ICheckoutGuestProfileModuleProps, nextState: ICheckoutGuestProfileState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const {
            config: { className },
            resources,
            moduleState: { isReady, hasExternalSubmitGroup }
        } = this.props;
        const { hasCheckoutError, errorMessage } = this.state;
        const email = this.props.data.checkout.result?.guestCheckoutEmail ?? '';

        const viewProps: ICheckoutGuestProfileViewProps = {
            ...this.props,
            ...this.state,
            phone: this.state.phone,
            phoneError: this.state.phoneError,
            checkoutErrorRef: this.checkoutErrorRef,
            onChange: this.onChange,
            onChangePhone: this.onChangePhone,
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit,
            loading: this.state.loading,

            moduleProps: {
                moduleProps: this.props,
                className: classnames('ms-checkout-guest-profile', className, isReady ? 'show' : 'add')
                // className: classnames('ms-checkout-guest-profile', className, 'add')
            },
            alert: hasCheckoutError && errorMessage && (
                <ErrorComponent {...{ title: resources.errorMessageTitle, message: errorMessage }} />
            ),
            showContactInfo: isReady
                ? getShowContactInfo({
                      canEdit: !hasExternalSubmitGroup,
                      email,
                      resources,
                      onEdit: this.onEdit
                  })
                : undefined,
            addContactInfo: !isReady
                ? getAddContactInfo({
                      email: this.state.email,
                      phone: this.state.phone,
                      hasError: this.hasEmailError,
                      resources,
                      inputRef: this.inputRef,
                      onChange: this.onChange,
                      onChangePhone: this.onChangePhone,
                      canSubmit: !hasExternalSubmitGroup,
                      onSubmit: this.onSubmit,
                      canCancel: !hasExternalSubmitGroup && !!email,
                      onCancel: this.onCancel
                  })
                : undefined
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private readonly init = async (): Promise<void> => {
        const checkoutState = this.props.data.checkout.result;
        const customerAccountInformation = this.props.data.accountInformation?.result;

        const hasInvoiceLine = checkoutState?.checkoutCart.hasInvoiceLine;
        let updateEmail: string = '';

        // if obo enabled and profile selected, we need to send the order status to obo user not store manager.
        if (Msdyn365.isOboRequest(this.props.context.request)) {
            updateEmail =
                customerAccountInformation && customerAccountInformation.Email !== undefined
                    ? customerAccountInformation.Email
                    : this.setErrorMsgOboUserEmailEmpty();
        } else {
            updateEmail = this.state.email;
        }

        const emailOnCart = checkoutState?.checkoutCart.cart.ReceiptEmail;
        const email = hasInvoiceLine
            ? this.props.context.request.user.emailAddress ?? ''
            : emailOnCart ??
              (checkoutState?.isExpressCheckoutApplied && !StringExtensions.isNullOrEmpty(checkoutState.emailAddressFromExpressCheckout)
                  ? checkoutState.emailAddressFromExpressCheckout
                  : undefined) ??
              updateEmail;
        const phoneOnCart = checkoutState?.checkoutCart.cart.AttributeValues?.filter((attribute: any) => {
            return attribute.Name === 'PHONE';
        });
        const savedPhone = sessionStorage.getItem('phone');
        //@ts-ignore
        if (phoneOnCart && phoneOnCart[0] && phoneOnCart[0].TextValue) {
            //@ts-ignore
            this.setState({ phone: phoneOnCart[0].TextValue });
        } else if (savedPhone) {
            this.setState({ phone: savedPhone });
        }
        const isPhoneValid = this.isPhoneRequiredAndValid(
            // @ts-ignore
            phoneOnCart && phoneOnCart[0] && phoneOnCart[0].TextValue ? phoneOnCart[0].TextValue : this.state.phone
        );
        let status: any;
        const isEmailValid = this.isEmailValid(email);
        if (phoneOnCart && phoneOnCart.length === 0) {
            status = 'updating';
        } else if (isPhoneValid && isEmailValid) {
            status = 'ready';
        } else if (!isPhoneValid && isEmailValid) {
            status = 'updating';
        } else if (isPhoneValid && !isEmailValid) {
            status = 'updating';
        } else {
            status = 'updating';
        }

        this.props.moduleState.init({
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit,
            //Original status line
            // status: hasInvoiceLine ? 'disabled' : email && isEmailValid ? 'ready' : 'updating',
            //Current status line but getting caught in refresh
            // status: 'updating',
            //Testing status line to allow pass through after refresh
            status: status,
            hasError: !!email && !isEmailValid
        });

        if (email) {
            await this.updateGuestProfile(email);
            checkoutState?.updateHasGuestCheckoutEmail({ newHasGuestCheckoutEmail: true });
        } else {
            checkoutState?.updateHasGuestCheckoutEmail({ newHasGuestCheckoutEmail: false });
        }
    };

    private readonly onChange = (rowEmail: string): void => {
        const email = (rowEmail || '').replace(new RegExp('[<>]', 'gi'), '');
        this.setState({ email });
        if (this.props.moduleState.hasError) {
            // Clear error during user updating the email
            this.props.moduleState.setHasError(false);
            this.setState({
                hasCheckoutError: false
            });
        }
    };

    private readonly onChangePhone = (rowPhone: string, country?: string): void => {
        if (this.state.phoneError === true) {
            this.setState({ phoneError: false });
        }
        let phoneNum: string = rowPhone;
        const cleanedInput = rowPhone.replace(/\D/g, '');

        if (country === 'KOR') {
            phoneNum = cleanedInput.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
        } else if (country === 'USA') {
            phoneNum = cleanedInput.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
        }
        this.setState({ phone: phoneNum });
        if (this.props.moduleState.hasError) {
            // Clear error during user updating the email
            this.props.moduleState.setHasError(false);
            this.setState({
                hasCheckoutError: false
            });
        }
    };

    private readonly setErrorMsgOboUserEmailEmpty = (): string => {
        // Clear prior error
        this.props.moduleState.setHasError(false);
        this.setState({
            hasCheckoutError: false
        });
        return '';
    };

    private readonly isEmailValid = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (this.props.config.validateEmailToggle && this.props.config.validateEmailToggle === true) {
            return EmailRegex.defaultRegex.test(email);
        }

        return emailRegex.test(email);
    };

    private readonly isPhoneRequiredAndValid = (phone?: string): boolean => {
        let isPhoneRequired = false;
        if (
            this.props.data.churchRetailChannelCountry &&
            this.props.data.churchRetailChannelCountry.result &&
            this.props.data.churchRetailChannelCountry.result[0] &&
            this.props.data.churchRetailChannelCountry.result[0].PHONENUMBERREQ &&
            this.props.data.churchRetailChannelCountry.result[0].PHONENUMBERREQ > 0
        ) {
            const phoneNumberReq = this.props.data.churchRetailChannelCountry.result[0].PHONENUMBERREQ;
            isPhoneRequired = phoneNumberReq === 1;
        }

        const phoneCheck = /^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,4}[-\s\.]?[0-9]{1,4}[-\s\.]?[0-9]{1,4}$/im;
        const doesPhonePass = this.state.phone ? phoneCheck.test(this.state.phone) : phone ? phoneCheck.test(phone) : '';
        if (isPhoneRequired) {
            return doesPhonePass ? true : false;
        } else {
            return true;
        }
    };

    private readonly updateGuestProfile = async (newGuestCheckoutEmail: string): Promise<void> => {
        if (this.props.data.checkout.result) {
            await this.props.data.checkout.result.updateGuestCheckoutEmail({ newGuestCheckoutEmail });
            this.setState({ email: newGuestCheckoutEmail });
        }
    };

    private readonly onEdit = (): void => {
        this.props.telemetry.information('Guest profile onEdit is called.');

        this.props.moduleState.onUpdating();
    };

    private setContactOnShippingCartLines = async (email: string | undefined, phone: string | undefined) => {
        const checkout = await this.props.data.checkout;
        let address: any;
        if (checkout && checkout.checkoutCart && checkout.checkoutCart.cart && checkout.checkoutCart.cart.CartLines) {
            address = checkout.checkoutCart.cart.CartLines[0].ShippingAddress;
        }
        if (address && address.Email !== email) {
            address.Phone = phone;
            address.Email = email;
            // const updatesEmail = await checkout?.checkoutCart.updateShippingAddress({ newShippingAddress: address });
            // await checkout.updateBillingAddress({ newBillingAddress: address });
            await checkout.updateShippingAddress({ newShippingAddress: address });
        }
    };

    private readonly updateContactAttributes = async (email?: string, phone?: string): Promise<any> => {
        const { addEmailForIframe } = this.props.config;

        if (addEmailForIframe && addEmailForIframe === true) {
            await this.setContactOnShippingCartLines(email, phone);
        }
    };

    private readonly onSubmit = async (): Promise<void> => {
        this.props.telemetry.information('Guest profile onSubmit is called.');

        const { email, phone } = this.state;
        this.setState({ loading: true });
        sessionStorage.setItem('phone', phone);
        sessionStorage.setItem('email', email);
        await this.submitEmail(email, phone);
        // await this.updateContactAttributes(email, phone);
        this.setState({ loading: false });
    };

    /**
     * Submit the email address.
     * @param email - The email to be submitted.
     *
     */
    private readonly submitEmail = async (email: string, phone?: string): Promise<void> => {
        const isValid = this.isEmailValid(email);
        const isPhoneValid = this.isPhoneRequiredAndValid();
        if (!isValid || !isPhoneValid) {
            if (!isValid) {
                this.props.moduleState.setHasError(true);
            }
            if (!isPhoneValid) {
                this.setState({ phoneError: true });
            }

            const input = this.inputRef.current?.focus && (this.inputRef.current as HTMLElement);
            input?.focus();
        } else {
            await this.updateContactAttributes(email, phone);
            await this.updateGuestProfile(email);
            this.props.moduleState.onReady();
        }
    };

    private readonly onCancel = (): void => {
        this.props.telemetry.information('Guest profile onCancel is called.');

        // Reset to the saved value
        if (!this.props.data.checkout.result) {
            return;
        }

        const email = this.props.data.checkout.result.guestCheckoutEmail;
        if (!email) {
            // It has no saved email, set to status updating and request user to fill the email form
            this.props.moduleState.onUpdating();
        } else {
            // It has saved email, set to status ready
            this.setState({
                email
            });
            this.props.moduleState.setHasError(false);
            this.setState({
                hasCheckoutError: false
            });
            this.props.moduleState.onReady();
        }
    };

    @action
    private readonly _setErrorMessage = (errorMessage: string): void => {
        this.props.telemetry.error(errorMessage);
        this.props.moduleState.setHasError(true);
        this.props.moduleState.onUpdating();
        this.setState({
            hasCheckoutError: true,
            errorMessage
        });
    };
}

export default CheckoutGuestProfile;
